import type { StaticImageData } from "next/image";

import android from "../assets/images/platform/android.webp";
import gameBoy from "../assets/images/platform/game-boy.png";
import ios from "../assets/images/platform/ios.webp";
import nintendoSwitch from "../assets/images/platform/nintendo-switch.webp";
import nintendoSwitch2 from "../assets/images/platform/nintendo-switch-2.webp";
import pc from "../assets/images/platform/pc.webp";
import playstation2 from "../assets/images/platform/playstation2.webp";
import playstation4 from "../assets/images/platform/playstation-4.webp";
import playstation5 from "../assets/images/platform/playstation-5.webp";
import stadia from "../assets/images/platform/stadia.webp";
import xboxOne from "../assets/images/platform/xbox-one.webp";
import xboxSeriesXS from "../assets/images/platform/xbox-series-x-s.webp";

/**
 * Platform icons bundled as static assets.
 *
 * The 12 platform tags each carry an `iconUrl` into R2, and the same dozen icons
 * sit on nearly every card and game page. Fetching them through the media Worker
 * costs an invocation (and a transformation per width) for files that never
 * change, so they are checked in here and ship from `/_next/static/media` with a
 * content hash and an immutable Cache-Control. Not `public/`: files without an
 * explicit rule there get `max-age=0, must-revalidate`.
 *
 * Keyed on the decoded R2 object key rather than the URL, because Mongo spells
 * the same key two ways — `platform%2FiconUrl%2F...` (GCS's publicUrl() ran the
 * object name through encodeURIComponent) and `platform/iconUrl/...`. Decoding
 * the path collapses both onto one key.
 *
 * Snapshot of GET /tag/platform on 2026-09-04. An icon re-uploaded in the admin
 * gets a fresh uuid, misses this map, and falls back to the media URL — so a
 * stale entry costs one request, never a broken image.
 */
const PLATFORM_ICONS: Record<string, StaticImageData> = {
  "platform/iconUrl/6/23/2025/5a155d0f-9eef-4cbf-b062-2b82ecc607a3.webp": nintendoSwitch2,
  "platform/iconUrl/12/9/2022/2ceae404-8732-4d72-9971-f67fab264520.webp": playstation2,
  "platform/iconUrl/9/7/2022/e74f88f7-d63a-4771-91d8-3146d863f634.webp": stadia,
  "platform/iconUrl/7/16/2022/999f3b06-c6e0-456f-a14e-3cd1c84a8107.png": gameBoy,
  "platform/iconUrl/9/21/2022/516cc318-1117-4121-831e-9fd5319e5544.webp": pc,
  "platform/iconUrl/9/21/2022/d1ad81c4-5fa3-4b2e-add4-2cd244ed2815.webp": ios,
  "platform/iconUrl/9/21/2022/f6dd7e6b-8f02-4677-b5c9-ceb51131bc46.webp": android,
  "platform/iconUrl/9/21/2022/2a1fc1c2-1f4c-42cf-bcb9-ad6a9e90e61a.webp": nintendoSwitch,
  "platform/iconUrl/9/21/2022/64efc10d-3758-40ca-877d-7678bdd44945.webp": xboxSeriesXS,
  "platform/iconUrl/9/21/2022/da0c5d0d-6d4a-486c-ba73-bb6eb6baf569.webp": xboxOne,
  "platform/iconUrl/9/21/2022/1f9a1afd-4aa7-4e90-9cca-8a9378aa6b67.webp": playstation4,
  "platform/iconUrl/9/21/2022/27235bf9-3026-4f3d-bec6-593c884cefcc.webp": playstation5,
};

/**
 * Object key for a media URL: the path with percent-encoding removed and the
 * leading slash dropped. `undefined` for anything that is not an absolute URL or
 * carries a malformed escape, so the caller falls through to the URL as-is.
 */
const toPlatformIconKey = (iconUrl: string): string | undefined => {
  try {
    return decodeURIComponent(new URL(iconUrl).pathname).replace(/^\/+/, "");
  } catch {
    return undefined;
  }
};

/**
 * Bundled static import for a platform `iconUrl`, or `undefined` when it is not
 * one of the 12 above — hand the URL to the image loader as before in that case.
 */
export const getPlatformIcon = (iconUrl: unknown): StaticImageData | undefined => {
  if (typeof iconUrl !== "string" || !iconUrl) return undefined;
  const key = toPlatformIconKey(iconUrl);
  return key ? PLATFORM_ICONS[key] : undefined;
};
